import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield,
  Download, 
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Target,
  X,
  Sparkles,
  Activity,
  Lock,
  AlertCircle
} from 'lucide-react';
import { generatePDF } from '../services/pdfGenerator';
import AIDocumentEditor from './AIDocumentEditor';

interface DiagnosticDocumentViewerProps {
  documents: {
    bilanSante: string;
    auditSecurite: string;
  };
  companyName: string;
  onClose: () => void;
}

type TabType = 'bilan' | 'audit';

export default function DiagnosticDocumentViewer({ documents, companyName, onClose }: DiagnosticDocumentViewerProps) {
  const [activeTab, setActiveTab] = useState<TabType>('bilan');
  
  // États pour les contenus éditables
  const [editableContents, setEditableContents] = useState({
    bilan: documents.bilanSante,
    audit: documents.auditSecurite
  });

  // État pour l'éditeur IA
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<'bilan' | 'audit'>('bilan');

  // Fonction pour ouvrir l'éditeur IA
  const openEditor = (docType: 'bilan' | 'audit') => {
    setEditingDocument(docType);
    setIsEditorOpen(true);
  };

  // Fonction pour mettre à jour le contenu
  const handleContentUpdate = (docType: 'bilan' | 'audit', newContent: string) => {
    setEditableContents(prev => ({
      ...prev,
      [docType]: newContent
    }));
  };

  const tabs = [
    { id: 'bilan' as TabType, label: 'Bilan de santé TI', icon: Activity, color: 'blue' },
    { id: 'audit' as TabType, label: 'Audit de sécurité', icon: Shield, color: 'red' },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col"
      >
        {/* Header avec gradient */}
        <div className="bg-gradient-to-r from-purple-600 via-bz-blue to-cyan-600 text-white p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-3xl font-bold mb-2">Diagnostic TI Complet</h2>
              <p className="text-white/90">{companyName}</p>
              <p className="text-white/70 text-sm mt-1">Documents normés COBIT/ITIL/NIST/ISO</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    relative flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap
                    ${isActive 
                      ? 'bg-white text-bz-dark shadow-lg' 
                      : 'bg-white/10 text-white hover:bg-white/20'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 bg-gray-50">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === 'bilan' && (
                <BilanSanteTIView 
                  content={editableContents.bilan} 
                  companyName={companyName}
                  onEdit={() => openEditor('bilan')}
                />
              )}
              {activeTab === 'audit' && (
                <AuditSecuriteView 
                  content={editableContents.audit} 
                  companyName={companyName}
                  onEdit={() => openEditor('audit')}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Éditeur IA */}
      <AIDocumentEditor
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        documentType={editingDocument}
        currentContent={editableContents[editingDocument]}
        onContentUpdate={(newContent) => handleContentUpdate(editingDocument, newContent)}
        companyName={companyName}
      />
    </div>
  );
}

// 📘 COMPOSANT : Bilan de santé TI
function BilanSanteTIView({ content, companyName, onEdit }: { content: string; companyName: string; onEdit: () => void }) {
  const handleDownloadPDF = async () => {
    await generatePDF(
      'bilan-content',
      `${companyName}_Bilan_de_sante_TI_${new Date().toISOString().split('T')[0]}.pdf`
    );
  };

  return (
    <div id="bilan-content" className="max-w-5xl mx-auto space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-2xl p-8 shadow-xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-4xl font-bold mb-2">Bilan de santé TI</h1>
            <p className="text-blue-100 text-lg">{companyName}</p>
            <p className="text-blue-200 text-sm mt-2">
              Rapport de maturité en gouvernance et gestion des services TI
            </p>
            <p className="text-blue-300 text-xs mt-1">
              Cadres normatifs : COBIT 2019 + ITIL 4 + ISO/IEC 20000
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-all shadow-lg hover:shadow-xl"
            >
              <Sparkles className="w-5 h-5" />
              Éditer avec IA
            </button>
            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl"
            >
              <Download className="w-5 h-5" />
              Télécharger PDF
            </button>
          </div>
        </div>

        {/* Stats rapides */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          <StatCard icon={Activity} label="Score global" value="2.8/5" color="blue" />
          <StatCard icon={CheckCircle} label="Piliers évalués" value="5" color="blue" />
          <StatCard icon={Target} label="Écarts identifiés" value="12" color="blue" />
          <StatCard icon={TrendingUp} label="Recommandations" value="8" color="blue" />
        </div>
      </div>

      {/* Contenu formaté du document */}
      <DocumentContent content={content} color="blue" />
    </div>
  );
}

// 🛡️ COMPOSANT : Audit de sécurité
function AuditSecuriteView({ content, companyName, onEdit }: { content: string; companyName: string; onEdit: () => void }) {
  const handleDownloadPDF = async () => {
    await generatePDF(
      'audit-content',
      `${companyName}_Audit_de_securite_${new Date().toISOString().split('T')[0]}.pdf`
    );
  };

  return (
    <div id="audit-content" className="max-w-5xl mx-auto space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-red-600 to-orange-600 text-white rounded-2xl p-8 shadow-xl">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-4xl font-bold mb-2">Audit de sécurité</h1>
            <p className="text-red-100 text-lg">{companyName}</p>
            <p className="text-red-200 text-sm mt-2">
              Analyse des écarts en cybersécurité et évaluation de la posture de sécurité
            </p>
            <p className="text-red-300 text-xs mt-1">
              Cadres normatifs : NIST CSF 2.0 + ISO/IEC 27001:2022 + CIS Controls v8
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-all shadow-lg hover:shadow-xl"
            >
              <Sparkles className="w-5 h-5" />
              Éditer avec IA
            </button>
            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 bg-white text-red-600 px-6 py-3 rounded-lg font-semibold hover:bg-red-50 transition-all shadow-lg hover:shadow-xl"
            >
              <Download className="w-5 h-5" />
              Télécharger PDF
            </button>
          </div>
        </div>

        {/* Stats rapides */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          <StatCard icon={Shield} label="Score sécurité" value="68/100" color="red" />
          <StatCard icon={Lock} label="Conformité" value="62%" color="red" />
          <StatCard icon={AlertTriangle} label="Risques critiques" value="5" color="red" />
          <StatCard icon={AlertCircle} label="Actions urgentes" value="8" color="red" />
        </div>
      </div>

      {/* Contenu formaté du document */}
      <DocumentContent content={content} color="red" />
    </div>
  );
}

// Composant pour afficher le contenu formaté
function DocumentContent({ content, color }: { content: string; color: string }) {
  // Nettoyer le contenu d'abord - enlever TOUS les markdown
  const cleanContent = content
    .replace(/\*\*\*(.+?)\*\*\*/g, '$1') // Gras+Italique
    .replace(/\*\*(.+?)\*\*/g, '$1') // Gras
    .replace(/\*(.+?)\*/g, '$1') // Italique
    .replace(/__(.+?)__/g, '$1'); // Underscore gras
  
  const lines = cleanContent.split('\n');
  const elements: React.JSX.Element[] = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i].trim();
    
    if (!line) continue;

    // Titres H3 avec ### (vérifier en premier car plus spécifique)
    if (line.startsWith('###')) {
      const text = line.replace(/^###\s*/, '').trim();
      elements.push(
        <h3 key={key++} className="text-xl font-semibold text-gray-800 mt-6 mb-3">
          {text}
        </h3>
      );
    }
    // Titres H2 avec ##
    else if (line.startsWith('##')) {
      const text = line.replace(/^##\s*/, '').trim();
      elements.push(
        <h2 key={key++} className="text-2xl font-bold text-gray-900 mt-8 mb-4 flex items-center gap-3">
          <div className="w-2 h-8 rounded-full" style={{ backgroundColor: getColor(color) }} />
          {text}
        </h2>
      );
    }
    // Titres H1 avec #
    else if (line.startsWith('#')) {
      const text = line.replace(/^#\s*/, '').trim();
      elements.push(
        <h1 key={key++} className="text-3xl font-bold text-gray-900 mt-10 mb-6 pb-3 border-b-4" style={{ borderColor: getColor(color) }}>
          {text}
        </h1>
      );
    }
    // Listes avec - ou • ou *
    else if (line.match(/^[-•*]\s/)) {
      const item = line.replace(/^[-•*]\s*/, '').trim();
      elements.push(
        <div key={key++} className="flex items-start gap-3 ml-4 my-2">
          <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: getColor(color) }} />
          <p className="text-gray-700 leading-relaxed flex-1">{item}</p>
        </div>
      );
    }
    // Listes numérotées
    else if (/^\d+\.\s/.test(line)) {
      const match = line.match(/^(\d+)\.\s*(.+)$/);
      if (match) {
        elements.push(
          <div key={key++} className="flex items-start gap-3 ml-4 my-2">
            <div className="flex items-center justify-center w-7 h-7 rounded-full text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: getColor(color) }}>
              {match[1]}
            </div>
            <p className="text-gray-700 leading-relaxed flex-1">{match[2]}</p>
          </div>
        );
      }
    }
    // Paragraphe normal
    else {
      elements.push(
        <p key={key++} className="text-gray-700 leading-relaxed my-3">
          {line}
        </p>
      );
    }
  }
  
  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="prose prose-lg max-w-none">
        {elements}
      </div>
      
      {/* Signature BZ */}
      <div className="mt-12 pt-6 border-t-2 border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-bz-blue to-purple-600 text-white rounded-xl p-4 shadow-lg">
              <Sparkles className="w-8 h-8" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-lg">Généré par PRIsME</p>
              <p className="text-sm text-gray-600">Excellence TI propulsée par l'IA</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-semibold text-bz-dark">Dany Bourassa</p>
            <p className="text-sm text-gray-600">BZ inc.</p>
            <p className="text-xs text-gray-500 mt-1">Consultant Expert TI</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function getColor(color: string): string {
  const colors: Record<string, string> = {
    blue: '#3b82f6',
    red: '#dc2626',
    green: '#10b981',
    purple: '#8b5cf6',
    orange: '#f97316'
  };
  return colors[color] || colors.blue;
}

// Composant StatCard
function StatCard({ icon: Icon, label, value }: { icon: any; label: string; value: string; color: string }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
      <Icon className="w-6 h-6 text-white mb-2" />
      <div className="text-2xl font-bold text-white mb-1">{value}</div>
      <div className="text-white/80 text-xs">{label}</div>
    </div>
  );
}





